import React, { useEffect, useRef } from "react"
import toast from "react-hot-toast"
import { useSportsWordleGame } from "~/hooks/useSportsWordleGame"

const SOCCER_LEAGUES = [
  { value: "Premier League", label: "Premier League" },
  { value: "La Liga", label: "La Liga" },
  { value: "Serie A", label: "Serie A" },
  { value: "Bundesliga", label: "Bundesliga" },
  { value: "Ligue 1", label: "Ligue 1" },
]

export default function SportsWordleGame() {
  const {
    gameState,
    gameMode,
    playerMode,
    selectedLeague,
    showStats,
    showRules,
    streak,
    gamesPlayed,
    winRate,
    setGameMode,
    setPlayerMode,
    setSelectedLeague,
    startNewGame,
    submitGuess,
    updateAutocomplete,
    selectAutocomplete,
    setShowStats,
    setShowRules,
    maxGuesses,
  } = useSportsWordleGame()

  const inputRef = useRef<HTMLInputElement>(null)

  // Show celebration or game over
  useEffect(() => {
    if (gameState.gameStatus === "won") {
      toast.success(`🎉 Correct! It was ${gameState.targetPlayer?.fullName}!`)
    } else if (gameState.gameStatus === "lost") {
      toast.error(`Game Over! The answer was ${gameState.targetPlayer?.fullName}`)
    }
  }, [gameState.gameStatus, gameState.targetPlayer])

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (gameState.currentInput.trim()) {
      submitGuess(gameState.currentInput.trim())
    }
  }

  // Get attribute display value and color
  const getAttributeDisplay = (attribute: string, value: any, comparison: any) => {
    let bgColor = "bg-gray-400"
    const textColor = "text-white"

    if (comparison === "correct") {
      bgColor = "bg-green-500"
    } else if (comparison === "close") {
      bgColor = "bg-yellow-500"
    }

    return { value, bgColor, textColor }
  }

  const renderGuessGrid = () => {
    const rows = []

    // Header row
    rows.push(
      <div key="header" className="grid grid-cols-6 gap-1 mb-2">
        <div className="text-xs font-bold text-center p-2 bg-foregroundx rounded">Team</div>
        <div className="text-xs font-bold text-center p-2 bg-foregroundx rounded">Pos</div>
        <div className="text-xs font-bold text-center p-2 bg-foregroundx rounded">##</div>
        <div className="text-xs font-bold text-center p-2 bg-foregroundx rounded">Ht</div>
        <div className="text-xs font-bold text-center p-2 bg-foregroundx rounded">Conf</div>
        <div className="text-xs font-bold text-center p-2 bg-foregroundx rounded">Years</div>
      </div>,
    )

    // Render completed guesses
    gameState.guesses.forEach((guess, i) => {
      const { player, comparison } = guess

      rows.push(
        <div key={i} className="grid grid-cols-6 gap-1 mb-1 animate-slideIn" style={{ animationDelay: `${i * 100}ms` }}>
          <div
            className={`p-2 rounded text-center text-sm font-medium ${
              comparison.team === "correct" ? "bg-green-500 text-white" : "bg-gray-400 text-white"
            }`}
          >
            {player.team}
          </div>
          <div
            className={`p-2 rounded text-center text-sm font-medium ${
              comparison.position === "correct" ? "bg-green-500 text-white" : "bg-gray-400 text-white"
            }`}
          >
            {player.position}
          </div>
          <div
            className={`p-2 rounded text-center text-sm font-medium ${
              comparison.jerseyNumber === "correct"
                ? "bg-green-500 text-white"
                : comparison.jerseyNumber === "close"
                  ? "bg-yellow-500 text-white"
                  : "bg-gray-400 text-white"
            }`}
          >
            {player.jerseyNumber || "?"}
          </div>
          <div
            className={`p-2 rounded text-center text-sm font-medium ${
              comparison.height === "correct"
                ? "bg-green-500 text-white"
                : comparison.height === "close"
                  ? "bg-yellow-500 text-white"
                  : "bg-gray-400 text-white"
            }`}
          >
            {player.height || "?"}
          </div>
          <div
            className={`p-2 rounded text-center text-sm font-medium ${
              comparison.conference === "correct" ? "bg-green-500 text-white" : "bg-gray-400 text-white"
            }`}
          >
            {player.conference || "?"}
          </div>
          <div
            className={`p-2 rounded text-center text-sm font-medium ${
              comparison.yearsActive === "correct"
                ? "bg-green-500 text-white"
                : comparison.yearsActive === "close"
                  ? "bg-yellow-500 text-white"
                  : "bg-gray-400 text-white"
            }`}
          >
            {player.yearsActive}
          </div>
        </div>,
      )
    })

    // Render empty rows
    const remainingRows = maxGuesses - gameState.guesses.length
    for (let i = 0; i < remainingRows; i++) {
      rows.push(
        <div key={`empty-${i}`} className="grid grid-cols-6 gap-1 mb-1">
          {[...Array(6)].map((_, j) => (
            <div key={j} className="p-2 rounded text-center text-sm font-medium bg-foregroundx">
              ?
            </div>
          ))}
        </div>,
      )
    }

    return rows
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold mb-2 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
          Sports Wordle
        </h1>
        <p className="text-primary">
          {gameMode === "basketball" ? "🏀 Guess the NBA Player" : "⚽ Guess the Soccer Player"}
        </p>
        <button onClick={() => setShowRules(true)} className="mt-2 text-sm text-primary hover:underline">
          How to Play
        </button>
      </div>

      {/* Game Settings */}
      <div className="bg-foregroundx rounded-lg p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Sport Mode */}
          <div>
            <label className="block text-sm font-medium text-primary mb-1">Sport</label>
            <select
              value={gameMode}
              onChange={(e) => setGameMode(e.target.value as any)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
            >
              <option value="basketball">🏀 Basketball</option>
              <option value="soccer">⚽ Soccer</option>
              <option value="all">🏀⚽ All Sports</option>
            </select>
          </div>

          {/* Player Era */}
          <div>
            <label className="block text-sm font-medium text-primary mb-1">Players</label>
            <select
              value={playerMode}
              onChange={(e) => setPlayerMode(e.target.value as any)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
            >
              <option value="current">Current Only</option>
              <option value="retired">Legends Only</option>
              <option value="all">All Players</option>
            </select>
          </div>

          {/* Soccer League Filter */}
          {gameMode === "soccer" && (
            <div>
              <label className="block text-sm font-medium text-primary mb-1">League</label>
              <select
                value={selectedLeague || ""}
                onChange={(e) => setSelectedLeague(e.target.value || undefined)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Leagues</option>
                {SOCCER_LEAGUES.map((league) => (
                  <option key={league.value} value={league.value}>
                    {league.label}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Legend */}
      <div className="mb-4 text-center text-sm text-primary">
        <span className="inline-flex items-center mr-4">
          <span className="w-4 h-4 bg-green-500 rounded mr-1"></span> Correct
        </span>
        <span className="inline-flex items-center mr-4">
          <span className="w-4 h-4 bg-yellow-500 rounded mr-1"></span> Close
        </span>
        <span className="inline-flex items-center">
          <span className="w-4 h-4 bg-gray-400 rounded mr-1"></span> Wrong
        </span>
      </div>

      {/* Guess Grid */}
      <div className="mb-8">{renderGuessGrid()}</div>

      {/* Input Form */}
      {gameState.gameStatus === "playing" && (
        <form onSubmit={handleSubmit} className="relative mb-8">
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <input
                ref={inputRef}
                type="text"
                value={gameState.currentInput}
                onChange={(e) => updateAutocomplete(e.target.value)}
                placeholder="Enter player name..."
                className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                autoComplete="off"
              />

              {/* Autocomplete dropdown */}
              {gameState.showAutocomplete && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-background border border-gray-300 rounded-md shadow-lg z-10">
                  {gameState.autocompleteResults.map((name, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => selectAutocomplete(name)}
                      className="w-full px-4 py-2 text-left  focus:bg-foreground"
                    >
                      {name}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              type="submit"
              className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium"
            >
              GUESS
            </button>
          </div>
        </form>
      )}

      {/* Hints */}
      {gameState.guesses.length > 0 && gameState.gameStatus === "playing" && (
        <div className="mb-4 p-3 bg-blue-50 rounded-lg text-center">
          <div className="text-sm text-blue-800">💡 Hint: Look for patterns in the colored attributes!</div>
        </div>
      )}

      {/* Stats Modal */}
      {showStats && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 animate-fadeIn">
          <div className="bg-background rounded-lg p-6 max-w-md w-full mx-4 animate-slideUp">
            <h2 className="text-2xl font-bold mb-4">
              {gameState.gameStatus === "won" ? "🎉 Congratulations!" : "😔 Better luck next time!"}
            </h2>

            <div className="mb-4">
              <p className="text-lg mb-2">The answer was:</p>
              <p className="text-2xl font-bold text-primary">{gameState.targetPlayer?.fullName}</p>
              <p className="text-sm text-primary mt-1">
                {gameState.targetPlayer?.position} • {gameState.targetPlayer?.team} • #
                {gameState.targetPlayer?.jerseyNumber}
              </p>
            </div>

            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="text-center">
                <div className="text-2xl font-bold">{gamesPlayed}</div>
                <div className="text-sm text-primary">Played</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold">{winRate}%</div>
                <div className="text-sm text-primary">Win Rate</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold">{streak}</div>
                <div className="text-sm text-primary">Streak</div>
              </div>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => setShowStats(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md  transition-colors"
              >
                Close
              </button>
              <button
                onClick={startNewGame}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                New Game
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rules Modal */}
      {showRules && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 animate-fadeIn overflow-y-auto">
          <div className="bg-background rounded-lg p-6 max-w-2xl w-full mx-4 my-8 animate-slideUp">
            <h2 className="text-2xl font-bold mb-4">How to Play Sports Wordle</h2>

            <div className="space-y-4 text-sm">
              <div>
                <h3 className="font-bold mb-2">Objective</h3>
                <p>
                  Guess the mystery NBA player in 6 tries using clues about their team, position, jersey number, height,
                  conference, and years active.
                </p>
              </div>

              <div>
                <h3 className="font-bold mb-2">Color Meanings</h3>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="w-4 h-4 bg-green-500 rounded"></span>
                    <span>
                      <strong>Green:</strong> Exact match
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-4 h-4 bg-yellow-500 rounded"></span>
                    <span>
                      <strong>Yellow:</strong> Close (within 5 for jersey #, within 2&quot; for height, overlapping
                      years)
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-4 h-4 bg-gray-400 rounded"></span>
                    <span>
                      <strong>Gray:</strong> Wrong
                    </span>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="font-bold mb-2">Attributes</h3>
                <ul className="list-disc list-inside space-y-1">
                  <li>
                    <strong>Team:</strong> The player&apos;s primary team
                  </li>
                  <li>
                    <strong>Position:</strong> G (Guard), F (Forward), or C (Center)
                  </li>
                  <li>
                    <strong>Jersey #:</strong> Yellow if within ±5
                  </li>
                  <li>
                    <strong>Height:</strong> Yellow if within ±2 inches
                  </li>
                  <li>
                    <strong>Conference:</strong> East or West
                  </li>
                  <li>
                    <strong>Years:</strong> Yellow if careers overlapped
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="font-bold mb-2">Tips</h3>
                <ul className="list-disc list-inside space-y-1">
                  <li>Start with versatile players to eliminate multiple attributes</li>
                  <li>Use the autocomplete to ensure correct spelling</li>
                  <li>Pay attention to yellow clues - they narrow down the possibilities</li>
                  <li>Consider the era when you see yellow for years</li>
                </ul>
              </div>
            </div>

            <button
              onClick={() => setShowRules(false)}
              className="mt-6 w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              Got it!
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// Add these animations to your global CSS or Tailwind config
const styles = `
@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideUp {
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.animate-slideIn {
  animation: slideIn 0.3s ease-out;
}

.animate-fadeIn {
  animation: fadeIn 0.3s ease-in-out;
}

.animate-slideUp {
  animation: slideUp 0.3s ease-out;
}
`
